/**
 * Pilot Errand Screen
 * Shows active errand and starts GPS tracking when errand begins
 * Displays route preview and errand details
 */

import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import { createPilotTracker } from '../services/PilotLocationTracker';
import './PilotDeliveryScreen.css';

// Fix Leaflet default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.3.1/images/marker-shadow.png',
});

const PilotDeliveryScreen = ({ errand, token, onDeliveryComplete }) => {
  const [isTracking, setIsTracking] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(null);
  const [distance, setDistance] = useState(0);
  const [speed, setSpeed] = useState(0);
  const [routeHistory, setRouteHistory] = useState([]);
  const [error, setError] = useState(null);
  const trackerRef = useRef(null);
  const locationIntervalRef = useRef(null);

  // Initialize tracker
  useEffect(() => {
    if (errand?.id && token) {
      trackerRef.current = createPilotTracker(errand.id, token);
    }

    return () => {
      if (trackerRef.current?.isCurrentlyTracking()) {
        trackerRef.current.stopTracking();
      }
    };
  }, [errand?.id, token]);

  // Start tracking delivery
  const handleStartDelivery = async () => {
    setError(null);

    try {
      // Get initial location
      const initialLocation = await trackerRef.current.getCurrentLocation();
      setCurrentLocation({
        latitude: initialLocation.latitude,
        longitude: initialLocation.longitude,
        accuracy: initialLocation.accuracy,
      });
      setRouteHistory([
        [initialLocation.latitude, initialLocation.longitude],
      ]);

      // Start GPS tracking
      const success = await trackerRef.current.startTracking();
      if (success) {
        setIsTracking(true);

        // Update UI with location changes every 2 seconds
        locationIntervalRef.current = setInterval(() => {
          const lastLoc = trackerRef.current.getLastLocation();
          if (lastLoc) {
            setCurrentLocation({
              latitude: lastLoc.latitude,
              longitude: lastLoc.longitude,
              accuracy: Math.round(lastLoc.accuracy),
              speed: lastLoc.speed,
            });

            // Update route history
            setRouteHistory((prev) => {
              const newPoint = [lastLoc.latitude, lastLoc.longitude];
              // Only add if it's a new point (not same as last)
              if (
                prev.length === 0 ||
                Math.abs(prev[prev.length - 1][0] - newPoint[0]) > 0.00001 ||
                Math.abs(prev[prev.length - 1][1] - newPoint[1]) > 0.00001
              ) {
                return [...prev, newPoint];
              }
              return prev;
            });

            // Calculate speed
            if (lastLoc.speed !== null) {
              setSpeed(Math.round(lastLoc.speed * 3.6)); // m/s to km/h
            }
          }
        }, 2000);
      }
    } catch (err) {
      setError(
        err.message || 'Failed to start delivery tracking. Check location permissions.'
      );
      console.error(err);
    }
  };

  // Stop tracking and complete delivery
  const handleCompleteDelivery = async () => {
    if (trackerRef.current?.isCurrentlyTracking()) {
      trackerRef.current.stopTracking();
    }
    if (locationIntervalRef.current) {
      clearInterval(locationIntervalRef.current);
    }
    setIsTracking(false);

    // Call parent callback
    if (onDeliveryComplete) {
      onDeliveryComplete({
        errandId: errand.id,
        finalLocation: currentLocation,
        routeHistory,
        totalDistance: distance,
      });
    }
  };

  // Calculate distance between two points (Haversine formula)
  const haversineDistance = (lat1, lon1, lat2, lon2) => {
    const R = 6371; // Earth's radius in km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // Calculate total distance from route history
  useEffect(() => {
    if (routeHistory.length > 1) {
      let totalDist = 0;
      for (let i = 1; i < routeHistory.length; i++) {
        totalDist += haversineDistance(
          routeHistory[i - 1][0],
          routeHistory[i - 1][1],
          routeHistory[i][0],
          routeHistory[i][1]
        );
      }
      setDistance(totalDist);
    }
  }, [routeHistory]);

  if (!currentLocation && !isTracking) {
    return (
      <div className="pilot-delivery-screen">
        <div className="delivery-card">
          <h2>📦 Delivery Details</h2>

          <div className="errand-info">
            <div className="info-row">
              <span className="label">Customer:</span>
              <span className="value">{errand?.customer_name}</span>
            </div>
            <div className="info-row">
              <span className="label">Pickup:</span>
              <span className="value">{errand?.pickup_location}</span>
            </div>
            <div className="info-row">
              <span className="label">Delivery:</span>
              <span className="value">{errand?.delivery_location}</span>
            </div>
            <div className="info-row">
              <span className="label">Amount:</span>
              <span className="value highlight">₦{errand?.amount}</span>
            </div>
          </div>

          {error && <div className="error-message">⚠️ {error}</div>}

          <button
            className="btn btn-primary btn-lg"
            onClick={handleStartDelivery}
            disabled={isTracking}
          >
            🚀 Start Delivery
          </button>

          <p className="info-text">
            Click to begin tracking and share your live location with the customer.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="pilot-delivery-screen">
      <div className="delivery-map">
        {currentLocation ? (
          <MapContainer
            center={[currentLocation.latitude, currentLocation.longitude]}
            zoom={15}
            style={{ height: '100%' }}
            key={`${currentLocation.latitude}-${currentLocation.longitude}`}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution='&copy; OpenStreetMap contributors'
            />

            {/* Route history polyline */}
            {routeHistory.length > 1 && (
              <Polyline
                positions={routeHistory}
                color="#FF6B6B"
                weight={3}
                opacity={0.7}
              />
            )}

            {/* Current location marker */}
            <Marker
              position={[currentLocation.latitude, currentLocation.longitude]}
              icon={L.icon({
                iconUrl: 'data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0Ij48Y2lyY2xlIGN4PSIxMiIgY3k9IjEyIiByPSIxMCIgZmlsbD0iI0ZGNkI2QiIvPjwvc3ZnPg==',
                iconSize: [24, 24],
                iconAnchor: [12, 12],
              })}
            >
              <Popup>
                <div className="location-popup">
                  <p>
                    <strong>Current Location</strong>
                  </p>
                  <p>
                    {currentLocation.latitude.toFixed(6)},
                    {currentLocation.longitude.toFixed(6)}
                  </p>
                  <p>Accuracy: ±{currentLocation.accuracy}m</p>
                </div>
              </Popup>
            </Marker>
          </MapContainer>
        ) : (
          <div className="map-loading">Loading map...</div>
        )}
      </div>

      <div className="delivery-stats">
        <div className="stats-header">
          <h3>📍 Live Tracking Active</h3>
          <div className="status-indicator pulse"></div>
        </div>

        <div className="stats-grid">
          <div className="stat-box">
            <div className="stat-label">Distance</div>
            <div className="stat-value">{distance.toFixed(2)} km</div>
          </div>

          <div className="stat-box">
            <div className="stat-label">Speed</div>
            <div className="stat-value">{speed} km/h</div>
          </div>

          <div className="stat-box">
            <div className="stat-label">Accuracy</div>
            <div className="stat-value">±{currentLocation?.accuracy || 0}m</div>
          </div>

          <div className="stat-box">
            <div className="stat-label">Points</div>
            <div className="stat-value">{routeHistory.length}</div>
          </div>
        </div>

        <div className="delivery-info">
          <p>
            <strong>Customer:</strong> {errand?.customer_name}
          </p>
          <p>
            <strong>To:</strong> {errand?.delivery_location}
          </p>
        </div>

        <div className="action-buttons">
          <button className="btn btn-success btn-lg" onClick={handleCompleteDelivery}>
            ✓ Complete Delivery
          </button>
        </div>

        <p className="info-text">
          Your location is being streamed to the customer in real-time.
        </p>
      </div>
    </div>
  );
};

export default PilotDeliveryScreen;
